import React, { useCallback, useMemo, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router";

import { ORGANIZATIONS, PROJECTS } from "../../../../platform/constants/routes";
import { ConfirmationDialog } from "../../../../shell/components/ConfirmationDialog";
import {
  ResourceIcon,
  ResourceLabel,
} from "../../../../shell/components/ResourceIcon";
import { useNotification } from "../../../../shell/contexts/notificationContext";
import {
  getContainerStyles,
  getStatusChipProps,
  sectionLabelSx,
} from "../../../../shell/styles/themeUtils";
import { QueueForm } from "../../components/CreateResourceDrawer/QueueForm/QueueForm";
import {
  useResourceMetrics,
  useResourceActivity,
  useResourceCredentials,
} from "../../hooks/useResourceDetail";
import { useResourceRealtime } from "../../hooks/useResourceRealtime";
import type { ResourceStatusTransition } from "../../hooks/useResourceRealtime";
import {
  useProjectResources,
  useResourceActions,
  useUpdateQueue,
} from "../../hooks/useResources";
import { useResourceSecretPermission } from "../../hooks/useResourceSecretPermission";
import type {
  NatsTransportSpec,
  ResourceActivityEntryUI,
  ResourceStatusUI,
} from "../../types";
import { copyToClipboard } from "../../utils/clipboard";
import {
  maskedConnString,
  parseEndpoint,
  realConnString,
} from "../../utils/connStringMask";
import { statusTransitionToast } from "../../utils/resourceStatusToasts";

import { ApplicationWiringSection } from "./components/ApplicationWiringSection";

function formatDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/**
 * Derive the PostgreSQL app role username from an endpoint string.
 * The provisioner names the role `{database_name}_app` where database_name
 * is the part after `/` in the endpoint. Used as a fallback when Vault
 * credentials are unavailable.
 */
function deriveUsername(endpoint: string | null): string | null {
  if (!endpoint) return null;
  const dbPart = endpoint.split("/")[1];
  if (!dbPart) return null;
  return `${dbPart}_app`;
}

/**
 * Human-readable label for a resource activity event.
 */
const ACTIVITY_EVENT_LABELS: Record<string, string> = {
  provisioning_started: "Provisioning started",
  vault_secret_written: "Vault secret written",
  gitops_committed: "GitOps manifest committed",
  database_ready: "Database ready",
  external_secret_ready: "External secret ready",
  provisioning_failed: "Provisioning failed",
  provisioning_retried: "Provisioning retried",
  resource_deprovisioned: "Resource deprovisioned",
};

function activityLabel(event: string): string {
  return ACTIVITY_EVENT_LABELS[event] ?? "Resource activity updated";
}

function activityDotColor(event: string): string {
  if (event === "provisioning_failed" || event === "resource_deprovisioned") {
    return "error.main";
  }
  if (event === "database_ready" || event === "external_secret_ready") {
    return "success.main";
  }
  return "text.secondary";
}

type ReadinessPhase = "waiting" | "timeout" | "failed" | "retrying" | "running";

interface ReadinessPhasePresentation {
  label: string;
  color: "default" | "warning" | "error" | "info" | "success";
  variant: "filled" | "outlined";
}

const READINESS_PHASE_PRESENTATION: Record<
  ReadinessPhase,
  ReadinessPhasePresentation
> = {
  waiting: {
    label: "Readiness waiting",
    color: "warning",
    variant: "outlined",
  },
  timeout: {
    label: "Readiness timed out",
    color: "error",
    variant: "outlined",
  },
  failed: { label: "Readiness failed", color: "error", variant: "outlined" },
  retrying: { label: "Readiness retrying", color: "info", variant: "outlined" },
  running: { label: "Readiness running", color: "success", variant: "filled" },
};

const READINESS_ACTIVITY_PHASES: Record<string, ReadinessPhase> = {
  readiness_waiting: "waiting",
  readiness_timeout: "timeout",
  readiness_failed: "failed",
  readiness_retrying: "retrying",
  readiness_running: "running",
};

function readinessPhaseForEvent(
  event: string,
): ReadinessPhasePresentation | null {
  const phase = READINESS_ACTIVITY_PHASES[event];
  return phase ? READINESS_PHASE_PRESENTATION[phase] : null;
}

// ── CopyField component ──────────────────────────────────────────────────────

interface CopyFieldProps {
  label: string;
  value: string;
  /** When true, value is masked as dots unless explicitly revealed. */
  maskable?: boolean;
  /** Monospace font for the value (default: true). */
  mono?: boolean;
  /**
   * Whether the current user is authorized to reveal or copy a secret
   * value. Required for maskable fields; non-maskable fields ignore it.
   * When false on a maskable field, the reveal and copy affordances are
   * hidden and only the masked placeholder is shown.
   */
  canRevealSecret?: boolean;
}

/**
 * A label + value row with a copy-to-clipboard button.
 * For password fields (maskable), includes a show/hide toggle gated by
 * the `canRevealSecret` permission. The masked placeholder is always
 * rendered by default; the real value appears only after an explicit,
 * authorized reveal action.
 */
function CopyField({
  label,
  value,
  maskable = false,
  mono = true,
  canRevealSecret = false,
}: CopyFieldProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const displayValue = maskable && !revealed ? "•".repeat(12) : value;

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  // Maskable fields require authorization to reveal or copy the real value.
  // When unauthorized, only the masked placeholder is shown with a lock
  // indicator — the real value never reaches the DOM or accessibility tree.
  const secretLocked = maskable && !canRevealSecret;

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="body2"
          fontFamily={mono ? "monospace" : "inherit"}
          sx={{
            wordBreak: "break-all",
            minWidth: 0,
            flex: 1,
            userSelect: "text",
          }}
        >
          {displayValue}
        </Typography>
        {maskable && canRevealSecret && (
          <IconButton
            size="small"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
          >
            {revealed ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </IconButton>
        )}
        {maskable && secretLocked && (
          <Tooltip title="Reveal or copy requires project write access">
            <span>
              <IconButton
                size="small"
                disabled
                aria-label={`${label} locked — reveal requires project write access`}
              >
                <LockOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {(!maskable || canRevealSecret) && (
          <IconButton
            size="small"
            onClick={handleCopy}
            aria-label={`Copy ${label}`}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {copied && (
        <Typography variant="caption" color="success.main">
          Copied to clipboard
        </Typography>
      )}
    </Box>
  );
}

// ── CopyableConnectionString component ───────────────────────────────────────

interface CopyableConnectionStringProps {
  label: string;
  /**
   * Masked, safe-to-render connection string. Always shown by default in
   * visible text and accessibility snapshots. Contains no credentials.
   */
  maskedValue: string;
  /**
   * Real (credential-bearing) connection string. Exposed ONLY through an
   * explicit, authorized reveal or copy action. Never rendered in a text
   * node, aria attribute, log, or analytics event.
   */
  realValue: string;
  /**
   * Whether the current user is authorized to reveal or copy the real
   * connection string. When false, only the masked value is shown with
   * a lock indicator and no reveal/copy affordance.
   */
  canRevealSecret: boolean;
}

/**
 * A highlighted connection string box with a masked default display and
 * an explicit, permission-gated reveal/copy interaction.
 *
 * The masked value is always rendered. The real value appears only after
 * an authorized reveal toggle and is copied only through an authorized
 * copy action. Unauthorized users see the mask and a lock indicator.
 */
function CopyableConnectionString({
  label,
  maskedValue,
  realValue,
  canRevealSecret,
}: CopyableConnectionStringProps): React.ReactElement {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // The displayed value is the masked string unless an authorized user
  // has explicitly toggled reveal. The real value never appears by default.
  const displayValue = canRevealSecret && revealed ? realValue : maskedValue;

  const handleCopy = async () => {
    // Only an authorized user may copy the real connection string. The
    // control is hidden below for unauthorized users as an additional
    // fail-closed guard.
    if (!canRevealSecret) return;
    const ok = await copyToClipboard(realValue);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontFamily="monospace"
            sx={{ color: "primary.main", wordBreak: "break-all" }}
          >
            {displayValue}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {canRevealSecret ? (
            <IconButton
              size="small"
              onClick={() => setRevealed((r) => !r)}
              aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
            >
              {revealed ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Tooltip title="Reveal or copy requires project write access">
              <span>
                <IconButton
                  size="small"
                  disabled
                  aria-label={`${label} locked — reveal requires project write access`}
                >
                  <LockOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canRevealSecret && (
            <IconButton
              size="small"
              onClick={handleCopy}
              aria-label={`Copy ${label}`}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {copied && (
        <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
          {canRevealSecret ? "Copied to clipboard" : "Copied masked value"}
        </Typography>
      )}
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export const ResourceDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orgAlias, projectAlias, resourceAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    resourceAlias: string;
  }>();

  const { resources, loading, error, reload } =
    useProjectResources(projectAlias);
  const { stop, restart, deprovision, retry, isActing } = useResourceActions();
  const { updateQueue: updateQueueFn, isSubmitting: isUpdating } =
    useUpdateQueue();
  const { showNotification } = useNotification();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);

  const resource = useMemo(
    () => resources.find((r) => r.alias === resourceAlias) ?? null,
    [resources, resourceAlias],
  );

  const { metrics, loading: metricsLoading } =
    useResourceMetrics(resourceAlias);
  const {
    activity,
    total: activityTotal,
    loading: activityLoading,
    error: activityError,
    reload: reloadActivity,
    loadMore,
    hasMore,
  } = useResourceActivity(resourceAlias);
  const {
    credentials,
    loading: credentialsLoading,
    error: credentialsError,
    reload: reloadCredentials,
  } = useResourceCredentials(resourceAlias, projectAlias);

  // Permission gate for revealing or copying connection secrets.
  // The API remains the authorization source of truth; this gate only
  // hides the reveal/copy affordance so secrets are never shown by default
  // to users who lack project write/admin or org admin.
  const { canRevealSecret } = useResourceSecretPermission(
    orgAlias,
    projectAlias,
  );

  const projectPath = projectAlias
    ? PROJECTS.DETAILS.getPath(orgAlias ?? "", projectAlias)
    : ORGANIZATIONS.ROOT.path;

  useResourceRealtime(
    projectAlias,
    reload,
    useCallback(
      (transition: ResourceStatusTransition) => {
        const toast = statusTransitionToast(transition);
        if (toast) {
          showNotification(toast.message, toast.severity);
        }
      },
      [showNotification],
    ),
  );

  const handleStop = async () => {
    if (!resource) return;
    await stop(resource.alias);
    reload();
  };

  const handleRestart = async () => {
    if (!resource) return;
    await restart(resource.alias);
    reload();
  };

  const handleRetry = async () => {
    if (!resource) return;
    await retry(resource.alias);
    reload();
  };

  const handleDelete = async () => {
    if (!resource) return;
    const ok = await deprovision(resource.alias);
    if (ok) {
      void navigate(projectPath);
    }
    setShowDeleteConfirm(false);
  };

  // Extract existing NATS stream specs from the resource for edit mode.
  const existingNatsStreams: NatsTransportSpec[] = useMemo(() => {
    if (!resource || resource.resourceType !== "nats") return [];
    const s = (resource.spec ?? {}) as Record<string, unknown>;
    if (Array.isArray(s.natsStreams))
      return s.natsStreams as NatsTransportSpec[];
    if (s.natsTransport) return [s.natsTransport as NatsTransportSpec];
    return [];
  }, [resource]);

  const handleUpdateQueue = async (req: {
    natsStreams: NatsTransportSpec[];
  }) => {
    if (!resource) return;
    if (req.natsStreams.length === 0) {
      showNotification("At least one new stream is required", "error");
      return;
    }
    const result = await updateQueueFn(resource.alias, req);
    if (result) {
      showNotification(
        `Added ${req.natsStreams.length} stream(s) to ${resource.name}`,
        "success",
      );
      setShowEditDialog(false);
      reload();
    }
  };

  const status = (resource?.status ?? "provisioning") as ResourceStatusUI;
  const statusProps = getStatusChipProps(status);
  const isRunning = status === "running";
  const isStopped = status === "stopped";
  const isFailed = status === "failed";
  const isNatsRunning = isRunning && resource?.resourceType === "nats";
  const spec = (resource?.spec ?? {}) as Record<string, unknown>;
  const errorMessage =
    typeof spec.errorMessage === "string" ? spec.errorMessage : null;
  // Effective endpoints: prefer resource.endpoint, fall back to
  // credentials.endpoint (the resource list API may return null when
  // provisioning didn't fully sync, but the credentials API has the value).
  const effectiveEndpoint = resource?.endpoint ?? credentials?.endpoint ?? null;
  const effectiveExternalEndpoint =
    resource?.externalEndpoint ?? credentials?.externalEndpoint ?? null;
  const endpointParts = parseEndpoint(effectiveEndpoint);
  const externalParts = parseEndpoint(effectiveExternalEndpoint);
  const derivedUsername = deriveUsername(effectiveEndpoint);
  const storagePct =
    metrics && metrics.storageLimitMB > 0
      ? Math.min(100, (metrics.storageUsedMB / metrics.storageLimitMB) * 100)
      : 0;

  // Build connection strings from real credentials when available.
  // The masked variant is always safe to render; the real variant is
  // exposed only through an explicit, authorized reveal/copy action.
  const internalConnStrings =
    credentials && effectiveEndpoint
      ? {
          masked: maskedConnString(credentials, effectiveEndpoint),
          real: realConnString(credentials, effectiveEndpoint),
        }
      : null;
  const externalConnStrings =
    credentials && effectiveExternalEndpoint
      ? {
          masked: maskedConnString(credentials, effectiveExternalEndpoint),
          real: realConnString(credentials, effectiveExternalEndpoint),
        }
      : null;

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Skeleton
          variant="rectangular"
          height={80}
          sx={{ mb: 3, borderRadius: 1 }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Skeleton
              variant="rectangular"
              height={260}
              sx={{ borderRadius: 1 }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton
              variant="rectangular"
              height={220}
              sx={{ borderRadius: 1 }}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error || !resource) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? "Resource not found"}
        </Alert>
        <Button
          component={RouterLink}
          to={projectPath}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to project
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      <Button
        component={RouterLink}
        to={projectPath}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Back to project
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color: "text.secondary" }}>
            <ResourceIcon type={resource.resourceType} size={28} />
          </Box>
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h5" fontWeight={700}>
                {resource.name}
              </Typography>
              <Chip
                label={statusProps.label}
                color={statusProps.color}
                variant={statusProps.variant}
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {ResourceLabel({ type: resource.resourceType })} ·{" "}
              {resource.provisionMode === "shared" ? "Shared" : "Dedicated"} ·{" "}
              {String(spec.region ?? "eu-central-1")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {isNatsRunning && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setShowEditDialog(true)}
            >
              Add Streams
            </Button>
          )}
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete resource
          </Button>
        </Stack>
      </Stack>

      {isFailed && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{ mb: 3 }}
          action={
            <Button
              color="error"
              size="small"
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={isActing}
            >
              Retry provisioning
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Provisioning failed
          </Typography>
          {errorMessage && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {errorMessage}
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          {/* ── NATS Resource (connection URL + .creds file) ── */}
          {resource.resourceType === "nats" && (
            <>
              {/* External NATS Access */}
              {(credentials?.externalUrl ?? effectiveExternalEndpoint) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ ...sectionLabelSx, mb: 1 }}
                  >
                    External Access
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Connect from outside the cluster using the external NATS
                    endpoint.
                  </Typography>

                  {credentialsError && !credentialsLoading && (
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={reloadCredentials}
                        >
                          Retry
                        </Button>
                      }
                    >
                      Credentials unavailable — Vault unreachable. Click retry
                      to try again.
                    </Alert>
                  )}

                  {credentialsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton variant="text" height={40} />
                      <Skeleton variant="text" height={40} />
                    </Stack>
                  ) : (
                    <>
                      <CopyableConnectionString
                        label="NATS URL"
                        maskedValue={
                          credentials?.externalUrl ??
                          effectiveExternalEndpoint ??
                          ""
                        }
                        realValue={
                          credentials?.externalUrl ??
                          effectiveExternalEndpoint ??
                          ""
                        }
                        canRevealSecret
                      />
                      {credentials?.creds && (
                        <Box sx={{ mt: 2 }}>
                          <CopyField
                            label="CREDENTIALS FILE (.creds)"
                            value={credentials.creds}
                            maskable
                            canRevealSecret={canRevealSecret}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              )}

              {/* Internal NATS Access */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  mb: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ ...sectionLabelSx, mb: 1 }}
                >
                  Internal Access
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Connect from inside the cluster (in-cluster DNS).
                </Typography>

                {credentialsLoading ? (
                  <Stack spacing={2}>
                    <Skeleton variant="text" height={40} />
                    <Skeleton variant="text" height={40} />
                  </Stack>
                ) : (
                  <>
                    <CopyableConnectionString
                      label="NATS URL"
                      maskedValue={credentials?.url ?? effectiveEndpoint ?? ""}
                      realValue={credentials?.url ?? effectiveEndpoint ?? ""}
                      canRevealSecret
                    />
                    {credentials?.creds && (
                      <Box sx={{ mt: 2 }}>
                        <CopyField
                          label="CREDENTIALS FILE (.creds)"
                          value={credentials.creds}
                          maskable
                          canRevealSecret={canRevealSecret}
                        />
                      </Box>
                    )}
                    {!credentials &&
                      !credentialsError &&
                      !credentialsLoading && (
                        <Alert severity="info" variant="outlined">
                          {status === "provisioning"
                            ? "Connection details will appear once provisioning is complete."
                            : "Credentials unavailable. The resource operator may still be reconciling this resource."}
                        </Alert>
                      )}
                  </>
                )}
              </Paper>
            </>
          )}

          {/* ── External Access (PostgreSQL — primary connection) ── */}
          {resource.resourceType !== "nats" && externalParts && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1 }}>
                External Access
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect from outside the cluster using the external endpoint.
              </Typography>

              {/* Credentials error state — shown when Vault is unreachable */}
              {credentialsError && !credentialsLoading && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={reloadCredentials}
                    >
                      Retry
                    </Button>
                  }
                >
                  Password unavailable — credentials service unreachable. Click
                  retry to try again.
                </Alert>
              )}

              {/* External connection string (only when credentials available) */}
              {externalConnStrings && (
                <CopyableConnectionString
                  label="CONNECTION STRING"
                  maskedValue={externalConnStrings.masked}
                  realValue={externalConnStrings.real}
                  canRevealSecret={canRevealSecret}
                />
              )}

              {/* Credential fields */}
              {credentialsLoading ? (
                <Stack spacing={2}>
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                  <Skeleton variant="text" height={40} />
                </Stack>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <CopyField label="HOST" value={externalParts.host} />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField label="PORT" value={externalParts.port} />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField
                      label="DATABASE"
                      value={
                        credentials?.database ??
                        effectiveEndpoint?.split("/")[1] ??
                        resource.alias
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CopyField
                      label="USERNAME"
                      value={credentials?.username ?? derivedUsername ?? "—"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {credentials ? (
                      <CopyField
                        label="PASSWORD"
                        value={credentials.password}
                        maskable
                        canRevealSecret={canRevealSecret}
                      />
                    ) : (
                      !credentialsLoading &&
                      !credentialsError && (
                        <Alert severity="info" variant="outlined">
                          {status === "provisioning"
                            ? "Connection details will appear once provisioning is complete."
                            : "Password unavailable — credentials could not be loaded. Click retry above."}
                        </Alert>
                      )
                    )}
                  </Grid>
                </Grid>
              )}
            </Paper>
          )}

          {/* ── Internal Access (PostgreSQL — collapsed for in-cluster debugging) ── */}
          {resource.resourceType !== "nats" && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: "pointer" }}
                onClick={() => setInternalExpanded((e) => !e)}
              >
                <IconButton
                  size="small"
                  sx={{
                    p: 0.25,
                    transform: internalExpanded
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: theme.transitions.create("transform"),
                  }}
                  aria-label={internalExpanded ? "Collapse" : "Expand"}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={sectionLabelSx}>
                  Internal Access
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  (for in-cluster debugging)
                </Typography>
              </Stack>
              <Collapse in={internalExpanded}>
                <Box sx={{ mt: 2 }}>
                  {/* Internal connection string (masked by default) */}
                  {internalConnStrings && (
                    <CopyableConnectionString
                      label="INTERNAL CONNECTION STRING"
                      maskedValue={internalConnStrings.masked}
                      realValue={internalConnStrings.real}
                      canRevealSecret={canRevealSecret}
                    />
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <CopyField
                        label="HOST"
                        value={endpointParts?.host ?? "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="PORT"
                        value={endpointParts?.port ?? "—"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="DATABASE"
                        value={
                          credentials?.database ??
                          effectiveEndpoint?.split("/")[1] ??
                          resource.alias
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CopyField
                        label="USERNAME"
                        value={credentials?.username ?? derivedUsername ?? "—"}
                      />
                    </Grid>
                    {credentials && (
                      <Grid item xs={12}>
                        <CopyField
                          label="PASSWORD"
                          value={credentials.password}
                          maskable
                          canRevealSecret={canRevealSecret}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          )}

          {/* ── Configuration ── */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Configuration
            </Typography>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Mode
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {resource.provisionMode === "shared" ? "Shared" : "Dedicated"}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Region
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {String(spec.region ?? "eu-central-1")}
                </Typography>
              </Stack>
              {typeof spec.backupRetentionDays === "number" && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Backup
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {`Daily automated backups retained for ${spec.backupRetentionDays} days`}
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(resource.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* ── Danger Zone ── */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              borderColor: "error.main",
              bgcolor: alpha(theme.palette.error.main, 0.04),
            }}
          >
            <Typography
              variant="subtitle2"
              color="error.main"
              fontWeight={700}
              sx={{ mb: 1 }}
            >
              Danger Zone
            </Typography>
            <Typography variant="body2" color="error.light" sx={{ mb: 2 }}>
              Deleting this resource removes the database, role, and Vault
              credentials. This cannot be undone.
            </Typography>
            <Button
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isActing}
            >
              Delete {ResourceLabel({ type: resource.resourceType })}
            </Button>
          </Paper>
        </Grid>

        {/* ── Right column: Metrics + Activity ── */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Metrics
            </Typography>

            {metricsLoading && !metrics ? (
              <Stack spacing={2}>
                <Skeleton variant="text" height={56} />
                <Skeleton
                  variant="rectangular"
                  height={6}
                  sx={{ borderRadius: 3 }}
                />
                <Skeleton variant="text" height={20} />
                <Skeleton variant="text" height={56} />
                <Skeleton variant="text" height={56} />
              </Stack>
            ) : metrics ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    STORAGE USED
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.storageUsedMB} MB
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={storagePct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.divider, 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        bgcolor: "primary.main",
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    of {metrics.storageLimitMB} MB logical limit
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    ACTIVE CONNECTIONS
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.activeConnections}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    QUERIES / SECOND
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {metrics.queriesPerSecond}
                  </Typography>
                </Box>
              </>
            ) : (
              <Alert severity="info" variant="outlined">
                Metrics are not available for this resource.
              </Alert>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
            }}
          >
            <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 2 }}>
              Recent Activity
            </Typography>

            {activityError ? (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button
                    color="error"
                    size="small"
                    onClick={reloadActivity}
                    disabled={activityLoading}
                  >
                    Retry
                  </Button>
                }
              >
                Failed to load resource activity.
              </Alert>
            ) : activityLoading && activity.length === 0 ? (
              <Stack spacing={2}>
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
              </Stack>
            ) : activity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No activity yet
              </Typography>
            ) : (
              <>
                <Stack spacing={2}>
                  {activity.map((entry: ResourceActivityEntryUI) => {
                    const readinessPhase = readinessPhaseForEvent(entry.event);
                    return (
                      <Stack
                        key={entry.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: activityDotColor(entry.event),
                            mt: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {activityLabel(entry.event)}
                          </Typography>
                          {readinessPhase && (
                            <Chip
                              label={readinessPhase.label}
                              color={readinessPhase.color}
                              variant={readinessPhase.variant}
                              size="small"
                              sx={{ mt: 0.75, fontWeight: 500 }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {formatDate(entry.createdAt)}
                          </Typography>
                          {entry.operationId && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Operation ID: {entry.operationId}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
                {hasMore && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={loadMore}
                    disabled={activityLoading}
                    sx={{ mt: 1, textTransform: "none" }}
                  >
                    {activityLoading
                      ? "Loading…"
                      : `Load more (${activity.length} of ${activityTotal})`}
                  </Button>
                )}
              </>
            )}
          </Paper>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-start",
            }}
          >
            {isRunning && (
              <Button
                variant="outlined"
                onClick={handleStop}
                disabled={isActing}
              >
                Stop resource
              </Button>
            )}
            {isStopped && (
              <Button
                variant="outlined"
                onClick={handleRestart}
                disabled={isActing}
              >
                Restart resource
              </Button>
            )}
          </Box>

          {/* Application Wiring Section */}
          {resource?.alias && (
            <ApplicationWiringSection resourceAlias={resource.alias} />
          )}
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={showDeleteConfirm}
        title={`Deprovision ${resource.name}?`}
        description="This will permanently deprovision the resource and delete all associated data. This action cannot be undone."
        confirmLabel="Deprovision"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Add Streams Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Streams to {resource?.name}</DialogTitle>
        <DialogContent>
          <QueueForm
            isEdit
            existingStreams={existingNatsStreams}
            resourceName={resource?.name ?? ""}
            isSubmitting={isUpdating}
            onSubmit={() => {}}
            onUpdateSubmit={handleUpdateQueue}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResourceDetailPage;
